/**
 * Hub CRM canonical entity linkage — **reuse these patterns** instead of re-deriving
 * company/contact/deal/communication joins in one-off screens or tools.
 *
 * **Companies** aggregate **contacts** (`contacts.company_id`).
 * **Deals** are **company-scoped** (`deals.company_id`). Open-pipeline lookups for a
 * contact use that contact's `company_id`. Chat tools mirror this via
 * `resolveCompanyIdFromContactId` (`apps/hub/lib/chat/tools/id-resolution.ts`).
 * **Communications** tie to **`contact_id`**; rows may also set **`company_id`**.
 * Company-wide activity views should union **contacts at the company** with
 * **company-linked** communications (same shape as GraphQL filters in Hub tables).
 *
 * Deal detail surfaces that reuse company-scoped integrations (communications, QuickBooks)
 * should read **`linkedCompanyAccount`** from `DetailContextProvider` when present — it mirrors
 * **`deals.company_id`** / `company_name` for the open deal (`DetailContextProvider.types.ts`).
 */

import {
  ACCOUNT_TIMELINE_STAGES,
  DEAL_WON_STAGE,
  LOST_STAGES,
  isLostStage,
} from "@/lib/constants/deal-stages";
import type { DealStage } from "@/lib/types/database/deals";

/** Stages excluded from **pipeline-only** forecasts (deal won + all lost reasons). */
export const CRM_PIPELINE_CLOSED_DEAL_STAGES: readonly DealStage[] = [
  DEAL_WON_STAGE,
  ...LOST_STAGES,
] as const;

/**
 * PostgREST `in` clause for `.not("stage", "in", ...)`.
 * **Enum columns require double-quoted values** — unquoted lists are rejected / unreliable.
 */
export function supabasePipelineClosedStagesNotInList(): string {
  return `(${CRM_PIPELINE_CLOSED_DEAL_STAGES.map((s) => `"${s}"`).join(",")})`;
}

/**
 * PostgREST `in` clause excluding ONLY lost stages — for forecast views that
 * keep WON + delivery deals (they count toward the period projection) and drop
 * only lost deals. See `lib/dashboard/metrics/forecast.ts` for why won deals count.
 */
export function supabaseLostStagesNotInList(): string {
  return `(${LOST_STAGES.map((s) => `"${s}"`).join(",")})`;
}

/**
 * Deal stages surfaced on **company/contact AI timeline** payloads (`relatedOpenDeals`).
 * Matches **Deals** tab breadth for the account (all lifecycle rows except **lost**), so closed-won
 * and post-win delivery (started → paid) appear as revenue context — not only pre-contract pipeline.
 *
 * Keep in sync with `deal_stage` in Postgres migrations.
 */
export const DEAL_STAGES_FOR_ACCOUNT_TIMELINE_SUMMARY: readonly DealStage[] =
  ACCOUNT_TIMELINE_STAGES;

export function isDealStageExcludedFromAccountTimeline(stage: string): boolean {
  return isLostStage(stage as DealStage);
}

/** Resolved company FK for querying open deals — null when the contact has no company */
export function companyIdForOpenDealsLookup(contact: {
  company_id?: string | null;
}): string | null {
  const id = contact.company_id;
  return typeof id === "string" && id.length > 0 ? id : null;
}

/**
 * GraphQL `customFilters` shape used by UniversalEntityPage communications tables
 * for “everything at this company”: rows whose `company_id` matches OR whose
 * `contact_id` is one of the CRM contacts under that company.
 */
export type CompanyScopedCommunicationsFilter =
  | { or: [{ company_id: { eq: string } }, { contact_id: { in: string[] } }] }
  | { company_id: string };

export function buildCompanyScopedCommunicationsFilter(
  companyId: string,
  contactIdsAtCompany: readonly string[]
): CompanyScopedCommunicationsFilter {
  const ids = [...contactIdsAtCompany].filter(Boolean);
  if (ids.length > 0) {
    return {
      or: [{ company_id: { eq: companyId } }, { contact_id: { in: ids } }],
    };
  }
  return { company_id: companyId };
}

/** Timeline AI summary defaults — kept aligned with `/api/ai/summary` expectations */
export const CONTACT_TIMELINE_SUMMARY_OPTIONS = {
  daysBack: 30,
  includeInternal: false,
  maxItems: 30,
  applyDaysBackToCommunications: false,
} as const;

export const COMPANY_TIMELINE_SUMMARY_OPTIONS = {
  daysBack: 30,
  includeInternal: false,
  maxItems: 80,
} as const;

/** Deal detail AI summary uses the same breadth as company (communications across the account). */
export const DEAL_TIMELINE_SUMMARY_OPTIONS = {
  ...COMPANY_TIMELINE_SUMMARY_OPTIONS,
} as const;
